/**
 * The install's files resolved straight out of the vault — no engine, no MEMFS.
 *
 * `files.ts` answers "what would the running engine see", and needs the booted
 * engine's filesystem to say it. The Mod Maker cannot wait for that: it opens
 * from the gate, before START, and its whole job is reading item stats out of
 * whatever base the player has. So this module builds the same `GameFiles`
 * shape over IndexedDB directly, with `Blob.slice` as the ranged read into the
 * DAT2 or DAT1 archives (`openAnyArchiveBlob` — Fallout 1's master.dat is the
 * older big-endian LZSS container, see `dat1.ts`).
 *
 * The precedence is the same chain `files.ts` documents, translated to vault
 * paths:
 *
 *   1. `data/<path>` vault entries — the loose directory xbase
 *   2. the mods layer per `mods/mods_order.txt`, later lines first; an entry
 *      is `mods/<e>` (a DAT2 blob) or a folder of `mods/<e>/<path>` entries
 *   3. `patchNNN.dat` highest first, `critter.dat`, `master.dat`
 *   4. any other vault entry at the path itself — the working-dir fallback
 *
 * One honest difference: the engine also sees files the *shell* writes at boot
 * (fallout2.cfg, and the Mod Maker's own compiled `mods/rr-recipes/` overlay).
 * Those are not in the vault, and this module deliberately does not fake them:
 * the Mod Maker wants the base a recipe would compile against, and a recipe's
 * own output must never be its input.
 *
 * Reads are cheap but not free (an inflate per member), so the result is
 * memoised until `resetVaultFilesCache()` — the vault changes shape only
 * through imports, which already know to reset.
 */

import { getFile as vaultGet, listFiles } from '../store';
import { openAnyArchiveBlob, type DatArchive } from './archive';
import { parseModsOrder, type GameFileHit, type GameFiles } from './files';

const decodeLatin1 = (bytes: Uint8Array): string => {
  let s = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  }
  return s;
};

const clean = (path: string): string => path.replace(/\\/g, '/').replace(/^\/+/, '').toLowerCase();

interface ModSource {
  name: string;
  read(path: string): Promise<Uint8Array | null>;
  list(prefix: string): string[];
}

const MAX_PATCH_INDEX = 32;

async function blobBytes(actualPath: string): Promise<Uint8Array | null> {
  const entry = await vaultGet(actualPath);
  return entry ? new Uint8Array(await entry.blob.arrayBuffer()) : null;
}

async function build(): Promise<GameFiles> {
  // One key map for every loose lookup: vault paths keep whatever case the
  // import gave them, lookups are lowercase.
  const all = await listFiles();
  const byLower = new Map<string, string>();
  for (const f of all) byLower.set(clean(f.path), f.path);

  const readVault = async (lowerPath: string): Promise<Uint8Array | null> => {
    const actual = byLower.get(lowerPath);
    return actual ? blobBytes(actual) : null;
  };

  // --- the mods layer -------------------------------------------------------
  const mods: ModSource[] = [];
  const orderBytes = await readVault('mods/mods_order.txt');
  if (orderBytes) {
    for (const entry of parseModsOrder(decodeLatin1(orderBytes))) {
      const lower = `mods/${entry.toLowerCase()}`;
      const actual = byLower.get(lower);
      if (actual) {
        const vaultEntry = await vaultGet(actual);
        const dat = vaultEntry ? await openAnyArchiveBlob(actual, vaultEntry.blob) : null;
        if (dat) {
          mods.push({ name: `mods/${entry}`, read: (p) => dat.read(p), list: (p) => dat.list(p) });
        }
        continue;
      }
      // A folder entry: any vault path under mods/<entry>/.
      const prefix = `${lower}/`;
      const members = [...byLower.keys()].filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
      if (members.length) {
        mods.push({
          name: `mods/${entry}`,
          read: (p) => readVault(prefix + clean(p)),
          list: (want) => members.filter((m) => m.startsWith(want)).sort(),
        });
      }
    }
    mods.reverse();
  }

  // --- the archives ---------------------------------------------------------
  const archives: DatArchive[] = [];
  const datNames: string[] = [];
  for (let i = MAX_PATCH_INDEX - 1; i >= 0; i--) datNames.push(`patch${String(i).padStart(3, '0')}.dat`);
  datNames.push('critter.dat', 'master.dat');
  for (const name of datNames) {
    const actual = byLower.get(name);
    if (!actual) continue;
    const entry = await vaultGet(actual);
    const dat = entry ? await openAnyArchiveBlob(actual, entry.blob) : null;
    if (dat) archives.push(dat);
  }

  const sources: string[] = [];
  if ([...byLower.keys()].some((k) => k.startsWith('data/'))) sources.push('data/');
  for (const m of mods) sources.push(m.name);
  for (const a of archives) sources.push(a.path);
  sources.push('install root');

  const cache = new Map<string, Promise<GameFileHit | null>>();

  const resolve = async (path: string): Promise<GameFileHit | null> => {
    const lower = clean(path);
    const loose = await readVault(`data/${lower}`);
    if (loose) return { bytes: loose, source: 'data/' };
    for (const mod of mods) {
      const bytes = await mod.read(lower);
      if (bytes) return { bytes, source: mod.name };
    }
    for (const archive of archives) {
      const bytes = await archive.read(lower);
      if (bytes) return { bytes, source: archive.path };
    }
    const root = await readVault(lower);
    return root ? { bytes: root, source: 'install root' } : null;
  };

  const readFrom = (path: string): Promise<GameFileHit | null> => {
    const key = clean(path);
    let pending = cache.get(key);
    if (!pending) {
      pending = resolve(path).catch(() => null);
      cache.set(key, pending);
    }
    return pending;
  };

  return {
    readFrom,
    async read(path) {
      return (await readFrom(path))?.bytes ?? null;
    },
    async readText(path) {
      const bytes = (await readFrom(path))?.bytes;
      return bytes ? decodeLatin1(bytes) : null;
    },
    list(prefix) {
      const want = clean(prefix);
      const seen = new Set<string>();
      for (const mod of mods) for (const member of mod.list(want)) seen.add(member);
      for (const archive of archives) for (const member of archive.list(want)) seen.add(member);
      return [...seen].sort();
    },
    sources,
    ok: archives.length > 0 || mods.length > 0,
  };
}

let cached: Promise<GameFiles> | null = null;

/** The vault's game files, memoised. Never rejects; `ok` is false when empty. */
export function vaultFiles(): Promise<GameFiles> {
  if (!cached) {
    cached = build().catch(() => ({
      read: async () => null,
      readFrom: async () => null,
      readText: async () => null,
      list: () => [],
      sources: [],
      ok: false,
    }));
  }
  return cached;
}

/** Drops the memo. Call after anything that changes the vault's shape. */
export function resetVaultFilesCache(): void {
  cached = null;
}
